import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import '../styles/ActionButtons.css';
import './CheckoutModal.css';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { SALON_API_PREFIX } from '../utils/apiConfig';
import { resolveSalonMediaUrl } from '../utils/mediaUrl';
import { sanitizeHtml, stripHtml } from '../utils/sanitizeHtml';
import { getFranchiseId, getFranchiseOrganizationId } from '../../shared/franchiseFlowAuth';
import { getNaturalsSalonModalRoot } from '../../../shared/modalRoots';
import {
  confirmAnalysisServicesAndStart,
  treatmentCartToPendingServices,
} from '../utils/analysisServiceSync';

const franchiseOrderScope = () => {
  const organization_id = getFranchiseOrganizationId() || undefined;
  const fid = getFranchiseId();
  return {
    ...(organization_id ? { organization_id } : {}),
    ...(fid ? { franchise_id: Number(fid) || fid } : {}),
  };
};

const formatPrice = (price) => `₹${price.toFixed(2)}`;

const buildCheckoutUrlFromCart = (items) => {
  if (!Array.isArray(items) || items.length === 0) return null;

  const variantPairs = [];
  let base = null;

  items.forEach((item) => {
    const raw = item.product || {};
    const qty = Math.max(1, Number(item.quantity) || 1);
    const variant = raw.variant_id || raw.variant || raw.product_variant || raw.id || raw.product_id || raw.sku;
    if (variant != null && String(variant).trim()) {
      variantPairs.push(`${String(variant).trim()}:${qty}`);
    }
    if (!base) {
      const cand = raw.checkout_url || raw.pdp_url || raw.product_link || raw.organization_domain_address;
      if (typeof cand === 'string' && cand.trim()) {
        base = cand.trim().replace(/\/$/, '');
      }
    }
  });

  if (base) {
    try {
      const parsed = new URL(base);
      if (parsed.protocol && parsed.host) {
        base = `${parsed.protocol}//${parsed.host}/cart`;
      }
    } catch {
      base = base.replace(/\/$/, '');
    }
  }

  if (base && variantPairs.length > 0) {
    return `${base}/${variantPairs.join(',')}`;
  }

  for (const item of items) {
    const raw = item.product || {};
    const cand = raw.checkout_url || raw.pdp_url || raw.product_link || raw.organization_domain_address;
    if (typeof cand === 'string' && cand.trim()) {
      return cand.trim();
    }
  }
  return null;
};

const toNumberOrNull = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const hasDiscountFlag = (discount) => {
  const d = toNumberOrNull(discount);
  return d != null && d > 0;
};

const hasProductNetPriceValue = (product) => toNumberOrNull(product?.amount) != null;
const hasProductAmountValue = (product) => toNumberOrNull(product?.amount) != null;
const hasTreatmentNetPriceValue = (treatment) => toNumberOrNull(treatment?.amount) != null;
const hasTreatmentAmountValue = (treatment) => toNumberOrNull(treatment?.amount) != null;

const getTreatmentUnitPriceNumber = (item) => {
  const t = item?.treatment || {};
  if (!hasTreatmentNetPriceValue(t)) return 0;
  const discount = toNumberOrNull(t.discount);
  const hasDiscount = hasDiscountFlag(discount);
  const sale = toNumberOrNull(t.sale_price);
  const net = toNumberOrNull(t.amount);

  if (hasDiscount && sale != null) return sale;
  return net ?? 0;
};

const toSafeHtml = (raw) => {
  if (raw == null) return '';
  const s = String(raw);
  const isHtml = /<[a-z][\s\S]*>/i.test(s);
  const normalized = isHtml ? s : s.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n/g, '<br />');
  return sanitizeHtml(normalized);
};

const toPlainText = (raw) => {
  if (raw == null) return '';
  return stripHtml(String(raw));
};

const toProductsArray = (productCartItemsMap) =>
  Object.entries(productCartItemsMap || {})
    .filter(([, v]) => v && v.quantity > 0)
    .map(([productId, v]) => ({ productId, product: v.product, quantity: v.quantity }));

const toTreatmentsArray = (treatmentCartItemsMap) =>
  Object.entries(treatmentCartItemsMap || {})
    .filter(([, v]) => v && v.quantity > 0)
    .map(([treatmentId, v]) => ({ treatmentId, treatment: v.treatment, quantity: v.quantity }));

const productsArrayToMap = (arr) => {
  const out = {};
  (arr || []).forEach((item) => {
    if (!item || !item.productId) return;
    if (!item.product || !item.quantity || item.quantity <= 0) return;
    out[item.productId] = { product: item.product, quantity: item.quantity };
  });
  return out;
};

const treatmentsArrayToMap = (arr) => {
  const out = {};
  (arr || []).forEach((item) => {
    if (!item || !item.treatmentId) return;
    if (!item.treatment || !item.quantity || item.quantity <= 0) return;
    out[item.treatmentId] = { treatment: item.treatment, quantity: item.quantity };
  });
  return out;
};

const getProductUnitNetPriceNumber = (item) => {
  const p = item?.product || {};
  const net = toNumberOrNull(p.amount);
  return net ?? 0;
};

const getProductUnitSalePriceNumber = (item) => {
  const p = item?.product || {};
  if (!hasProductNetPriceValue(p)) return 0;
  const hasDiscount = hasDiscountFlag(p.discount);
  const sale = toNumberOrNull(p.sale_price);
  const net = getProductUnitNetPriceNumber(item);
  if (hasDiscount && sale != null) return sale;
  // If discount flag missing but sale is valid lower price, still honor it
  if (sale != null && sale > 0 && sale < net) return sale;
  return net;
};

const getTreatmentUnitNetPriceNumber = (item) => {
  const t = item?.treatment || {};
  const net = toNumberOrNull(t.amount);
  return net ?? 0;
};

const getResolvedBaseUrl = (apiBaseUrl) =>
  (
    apiBaseUrl ||
    (typeof window !== 'undefined' &&
      (window.NYAFAZ_API_URL || window.REACT_APP_API_URL || window.location?.origin)) ||
    ''
  ).replace(/\/$/, '');

const CombinedCheckoutModal = ({
  isOpen,
  onClose,
  productCartItemsMap,
  treatmentCartItemsMap,
  onProductCartUpdate,
  onTreatmentCartUpdate,
  sessionId,
  apiBaseUrl,
  token,
  mode = 'all',
}) => {
  const productsOnly = mode === 'products';
  const treatmentsOnly = mode === 'treatments';
  const initialProducts = useMemo(
    () => (treatmentsOnly ? [] : toProductsArray(productCartItemsMap)),
    [productCartItemsMap, treatmentsOnly],
  );
  const initialTreatments = useMemo(
    () => (productsOnly ? [] : toTreatmentsArray(treatmentCartItemsMap)),
    [treatmentCartItemsMap, productsOnly],
  );

  const [productItems, setProductItems] = useState([]);
  const [treatmentItems, setTreatmentItems] = useState([]);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [showProceedConfirmModal, setShowProceedConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setProductItems(initialProducts);
    setTreatmentItems(initialTreatments);
  }, [isOpen, initialProducts, initialTreatments]);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const productsTotal = productItems.reduce(
    (sum, item) => sum + getProductUnitSalePriceNumber(item) * (item.quantity || 0),
    0
  );
  const productsNetTotal = productItems.reduce(
    (sum, item) => sum + getProductUnitNetPriceNumber(item) * (item.quantity || 0),
    0
  );
  const treatmentsTotal = treatmentItems.reduce(
    (sum, item) => sum + getTreatmentUnitPriceNumber(item) * (item.quantity || 0),
    0
  );

  const treatmentsNetTotal = treatmentItems.reduce(
    (sum, item) => sum + getTreatmentUnitNetPriceNumber(item) * (item.quantity || 0),
    0
  );

  const subtotalNet = productsNetTotal + treatmentsNetTotal;
  const grandTotal = productsTotal + treatmentsTotal;
  const totalDiscountAmount = Math.max(0, subtotalNet - grandTotal);
  const hasPricedProducts = productItems.some((item) => hasProductNetPriceValue(item?.product));
  const hasPricedTreatments = treatmentItems.some((item) => hasTreatmentNetPriceValue(item?.treatment));
  const hasAnyPricedItems = hasPricedProducts || hasPricedTreatments;

  const syncBack = (nextProducts, nextTreatments) => {
    if (!treatmentsOnly && onProductCartUpdate) onProductCartUpdate(productsArrayToMap(nextProducts));
    if (!productsOnly && onTreatmentCartUpdate) onTreatmentCartUpdate(treatmentsArrayToMap(nextTreatments));
  };

  const handleClose = () => {
    syncBack(productItems, treatmentItems);
    onClose();
  };

  const handleCancelSuggestions = () => {
    handleClose();
  };

  const handleOpenProceedConfirmModal = () => {
    if (isPlacingOrder) return;
    setShowProceedConfirmModal(true);
  };

  const handleCloseProceedConfirmModal = () => {
    if (isPlacingOrder) return;
    setShowProceedConfirmModal(false);
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    handleClose();
  };

  const changeQty = (kind, index, delta) => {
    if (kind === 'product') {
      setProductItems((prev) => {
        const next = [...prev];
        const cur = next[index];
        if (!cur) return prev;
        const q = (cur.quantity || 0) + delta;
        if (q <= 0) next.splice(index, 1);
        else next[index] = { ...cur, quantity: q };
        syncBack(next, treatmentItems);
        return next;
      });
      return;
    }
    setTreatmentItems((prev) => {
      const next = [...prev];
      const cur = next[index];
      if (!cur) return prev;
      const q = (cur.quantity || 0) + delta;
      if (q <= 0) next.splice(index, 1);
      else next[index] = { ...cur, quantity: q };
      syncBack(productItems, next);
      return next;
    });
  };

  const handlePlaceOrder = async () => {
    setShowProceedConfirmModal(false);
    setIsPlacingOrder(true);
    try {
      const now = new Date();
      const pad2 = (value) => String(value).padStart(2, '0');
      const offsetMinutes = -now.getTimezoneOffset();
      const offsetSign = offsetMinutes >= 0 ? '+' : '-';
      const offsetAbs = Math.abs(offsetMinutes);
      const offsetHour = pad2(Math.floor(offsetAbs / 60));
      const offsetMin = pad2(offsetAbs % 60);
      const localIsoWithOffset =
        `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}` +
        `T${pad2(now.getHours())}:${pad2(now.getMinutes())}:${pad2(now.getSeconds())}` +
        `${offsetSign}${offsetHour}:${offsetMin}`;
      const placedAtPayload = {
        placed_at_local: localIsoWithOffset,
        placed_timezone:
          (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || 'UTC',
        placed_timezone_offset_minutes: offsetMinutes,
        placed_at_epoch_ms: now.getTime(),
      };

      const baseUrl = getResolvedBaseUrl(apiBaseUrl);

      if (sessionId && baseUrl) {
        if (!productsOnly && treatmentItems.length > 0) {
          const placedTreatments = treatmentItems.map((item) => ({
            treatment: item.treatment,
            quantity: item.quantity,
          }));
          const tr = await fetchWithTimeout(`${baseUrl}${SALON_API_PREFIX}/save-placed-order-treatments`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              skin_id: sessionId,
              session_id: sessionId,
              ...(token && { app_session_token: token }),
              placed_order_treatments: placedTreatments,
              create_new_order: true,
              ...placedAtPayload,
              ...franchiseOrderScope(),
            }),
            timeoutMs: 20000,
          });
          if (!tr.ok) {
            const errData = await tr.json().catch(() => ({}));
            console.warn('Could not save placed order (treatments) for tracking:', errData);
          }
        }

        if (!treatmentsOnly && productItems.length > 0) {
          const placedProducts = productItems.map((item) => ({
            product: item.product,
            quantity: item.quantity,
          }));
          const checkoutUrl = buildCheckoutUrlFromCart(placedProducts);
          const pr = await fetchWithTimeout(`${baseUrl}${SALON_API_PREFIX}/save-placed-order-products`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              skin_id: sessionId,
              session_id: sessionId,
              ...(token && { app_session_token: token }),
              placed_order_products: placedProducts,
              create_new_order: true,
              ...placedAtPayload,
              ...franchiseOrderScope(),
            }),
            timeoutMs: 20000,
          });
          let resolvedCheckoutUrl = checkoutUrl;
          if (!pr.ok) {
            const errData = await pr.json().catch(() => ({}));
            console.warn('Could not save placed order (products) for tracking:', errData);
          } else {
            const placedResponseJson = await pr.json().catch(() => ({}));
            resolvedCheckoutUrl = placedResponseJson?.checkout_url || checkoutUrl;
          }
          if (resolvedCheckoutUrl) {
            try {
              window.open(resolvedCheckoutUrl, '_blank', 'noopener,noreferrer');
            } catch {
              // stay on page if popup is blocked
            }
          }
        }
      }

      if (!productsOnly && treatmentItems.length > 0) {
        try {
          const cartMap = {};
          treatmentItems.forEach((item, index) => {
            const key = String(item.treatmentId || item.treatment?.id || index);
            cartMap[key] = {
              treatment: item.treatment,
              quantity: item.quantity,
            };
          });
          const lines = treatmentCartToPendingServices(cartMap, 'skin_analysis');
          await confirmAnalysisServicesAndStart(lines, 'skin_analysis');
        } catch (syncErr) {
          console.warn('Could not start confirmed treatments on visit:', syncErr);
        }
      }

      setShowSuccessModal(true);
    } catch (err) {
      console.error('Place order error:', err);
      // Keep UX consistent even if tracking save fails.
      setShowSuccessModal(true);
    } finally {
      setIsPlacingOrder(false);
    }
  };

  if (!isOpen) return null;

  const isEmpty = productItems.length === 0 && treatmentItems.length === 0;

  return createPortal(
    <div
      className="checkout-modal-overlay"
      onClick={() => {
        if (showProceedConfirmModal || showSuccessModal || isPlacingOrder) return;
        handleClose();
      }}
    >
      <div className="checkout-modal-container combined-checkout-modal" onClick={(e) => e.stopPropagation()}>
        <div className="checkout-modal-header">
          <h2 className="checkout-modal-title">
            {productsOnly ? 'Product Checkout' : treatmentsOnly ? 'Treatment Checkout' : 'Confirm Suggestions'}
          </h2>
          <button
            type="button"
            className="checkout-modal-close-button"
            onClick={handleClose}
            aria-label="Close suggestions modal"
            disabled={showProceedConfirmModal || showSuccessModal || isPlacingOrder}
          >
            ×
          </button>
        </div>

        <div className="checkout-modal-content">
          {isEmpty ? (
            <div className="checkout-empty-cart">
              <div className="checkout-empty-cart-content">
                <div className="checkout-empty-cart-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="9" cy="21" r="1"></circle>
                    <circle cx="20" cy="21" r="1"></circle>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                  </svg>
                </div>
                <h3 className="checkout-empty-cart-title">
                  {productsOnly ? 'No products in your cart' : treatmentsOnly ? 'No treatments in your cart' : 'No items in your cart'}
                </h3>
                <p className="checkout-empty-cart-message">
                  {productsOnly
                    ? 'Add products to continue checkout'
                    : treatmentsOnly
                      ? 'Add treatments to confirm'
                      : 'Add treatments or products to confirm suggestions'}
                </p>
              </div>
            </div>
          ) : (
            <>
              {treatmentItems.length > 0 && (
                <div className="checkout-modal-items-section">
                  <h3 className="checkout-modal-section-title">Treatments</h3>
                  <div className="checkout-modal-items-list">
                    {treatmentItems.map((item, index) => {
                      const t = item.treatment;
                      const showPrice = hasTreatmentNetPriceValue(t);
                      const showQty = hasTreatmentAmountValue(t);
                      const unit = getTreatmentUnitPriceNumber(item);
                      const unitNet = getTreatmentUnitNetPriceNumber(item);
                      const itemTotal = unit * item.quantity;
                      const itemNetTotal = unitNet * item.quantity;
                      const itemDiscountAmount = Math.max(0, itemNetTotal - itemTotal);
                      const discount = toNumberOrNull(t?.discount);
                      const hasDiscount = showPrice && discount != null && discount > 0 && itemDiscountAmount > 0;
                      return (
                        <div key={item.treatmentId || index} className="checkout-modal-item-card">
                          <div className="checkout-modal-item-image">
                            {t?.image_url ? (
                              <img
                                src={resolveSalonMediaUrl(t.image_url)}
                                alt={t.treatment || t.name || 'Treatment'}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="checkout-modal-item-image-placeholder">
                                <span>No Image</span>
                              </div>
                            )}
                          </div>
                          <div className="checkout-modal-item-details">
                            <h4 className="checkout-modal-item-name">{t?.treatment || t?.name || 'Treatment'}</h4>
                            <div className="checkout-modal-item-meta">
                              {showPrice ? (
                                <div className="checkout-modal-item-price">
                                  <span className="checkout-modal-meta-label">Price:</span>
                                  <span className="checkout-modal-meta-value">
                                    {hasDiscount ? (
                                      <>
                                        <span
                                          style={{
                                            textDecoration: 'line-through',
                                            textDecorationThickness: 'max(3px, 0.12em)',
                                            opacity: 0.65,
                                            marginRight: 8,
                                          }}
                                        >
                                          {formatPrice(itemNetTotal)}
                                        </span>
                                        <span style={{ fontWeight: 800 }}>
                                          {formatPrice(itemTotal)}
                                        </span>
                                      </>
                                    ) : (
                                      <span style={{ fontWeight: 800 }}>{formatPrice(itemTotal)}</span>
                                    )}
                                  </span>
                                </div>
                              ) : null}
                              {showQty ? (
                                <div className="checkout-modal-item-quantity-controls">
                                  <span className="checkout-modal-meta-label">Sessions:</span>
                                  <div className="checkout-modal-quantity-counter">
                                    <button
                                      type="button"
                                      className="checkout-modal-quantity-button checkout-modal-quantity-decrease"
                                      onClick={() => changeQty('treatment', index, -1)}
                                      aria-label="Decrease quantity"
                                    >
                                      −
                                    </button>
                                    <span className="checkout-modal-quantity-value">{item.quantity}</span>
                                    <button
                                      type="button"
                                      className="checkout-modal-quantity-button checkout-modal-quantity-increase"
                                      onClick={() => changeQty('treatment', index, +1)}
                                      aria-label="Increase quantity"
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>
                              ) : null}
                            </div>
                            {hasDiscount ? (
                              <div className="checkout-modal-item-meta" style={{ marginTop: 6 }}>
                                <div className="checkout-modal-item-price">
                                  <span className="checkout-modal-meta-label">You save:</span>
                                  <span className="checkout-modal-meta-value" style={{ fontWeight: 800 }}>
                                    {formatPrice(itemDiscountAmount)}
                                  </span>
                                </div>
                              </div>
                            ) : null}
                          </div>
                          {showPrice ? (
                            <div className="checkout-modal-item-total">
                              <span className="checkout-modal-item-total-label">Item Total</span>
                              <span className="checkout-modal-item-total-value">{formatPrice(itemTotal)}</span>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {productItems.length > 0 && (
                <div className="checkout-modal-items-section">
                  <h3 className="checkout-modal-section-title">Products</h3>
                  <div className="checkout-modal-items-list">
                    {productItems.map((item, index) => {
                      const p = item.product;
                      const showPrice = hasProductNetPriceValue(p);
                      const showQty = hasProductAmountValue(p);
                      const unit = getProductUnitSalePriceNumber(item);
                      const unitNet = getProductUnitNetPriceNumber(item);
                      const itemTotal = unit * item.quantity;
                      const itemNetTotal = unitNet * item.quantity;
                      const itemDiscountAmount = Math.max(0, itemNetTotal - itemTotal);
                      const hasDiscount = itemDiscountAmount > 0 && itemTotal > 0 && itemNetTotal > itemTotal;
                      const productDescPlain = toPlainText(p?.description);
                      const productDescHtml = toSafeHtml(p?.description);
                      return (
                        <div key={item.productId || index} className="checkout-modal-item-card">
                          <div className="checkout-modal-item-image">
                            {p?.image_url ? (
                              <img
                                src={resolveSalonMediaUrl(p.image_url)}
                                alt={p.product_name || 'Product'}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="checkout-modal-item-image-placeholder">
                                <span>No Image</span>
                              </div>
                            )}
                          </div>
                          <div className="checkout-modal-item-details">
                            <h4 className="checkout-modal-item-name">{p?.product_name || p?.name || 'Product'}</h4>
                            {productDescPlain ? (
                              <p
                                className="checkout-modal-item-description"
                                title={productDescPlain}
                                dangerouslySetInnerHTML={{ __html: productDescHtml }}
                              />
                            ) : null}
                            <div className="checkout-modal-item-meta">
                              {showPrice ? (
                                <div className="checkout-modal-item-price">
                                  <span className="checkout-modal-meta-label">Price:</span>
                                  <span className="checkout-modal-meta-value">
                                    {hasDiscount ? (
                                      <>
                                        <span
                                          style={{
                                            textDecoration: 'line-through',
                                            textDecorationThickness: 'max(3px, 0.12em)',
                                            opacity: 0.65,
                                            marginRight: 8,
                                          }}
                                        >
                                          {formatPrice(itemNetTotal)}
                                        </span>
                                        <span style={{ fontWeight: 800 }}>{formatPrice(itemTotal)}</span>
                                      </>
                                    ) : (
                                      <span style={{ fontWeight: 800 }}>{formatPrice(itemTotal)}</span>
                                    )}
                                  </span>
                                </div>
                              ) : null}
                              {showQty ? (
                                <div className="checkout-modal-item-quantity-controls">
                                  <span className="checkout-modal-meta-label">Quantity:</span>
                                  <div className="checkout-modal-quantity-counter">
                                    <button
                                      type="button"
                                      className="checkout-modal-quantity-button checkout-modal-quantity-decrease"
                                      onClick={() => changeQty('product', index, -1)}
                                      aria-label="Decrease quantity"
                                    >
                                      −
                                    </button>
                                    <span className="checkout-modal-quantity-value">{item.quantity}</span>
                                    <button
                                      type="button"
                                      className="checkout-modal-quantity-button checkout-modal-quantity-increase"
                                      onClick={() => changeQty('product', index, +1)}
                                      aria-label="Increase quantity"
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>
                              ) : null}
                            </div>
                            {hasDiscount ? (
                              <div className="checkout-modal-item-meta" style={{ marginTop: 6 }}>
                                <div className="checkout-modal-item-price">
                                  <span className="checkout-modal-meta-label">You save:</span>
                                  <span className="checkout-modal-meta-value" style={{ fontWeight: 800 }}>
                                    {formatPrice(itemDiscountAmount)}
                                  </span>
                                </div>
                              </div>
                            ) : null}
                          </div>
                          {showPrice ? (
                            <div className="checkout-modal-item-total">
                              <span className="checkout-modal-item-total-label">Item Total</span>
                              <span className="checkout-modal-item-total-value">{formatPrice(itemTotal)}</span>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="checkout-modal-footer">
          <div className="combined-checkout-footer-grid">
            <div className="combined-checkout-summary-card" aria-label="Order summary">
              <div className="combined-checkout-summary-header">
                <span className="combined-checkout-summary-title">Suggestion summary</span>
              </div>
              {hasAnyPricedItems ? (
                <>
                  {hasPricedTreatments && (
                    <div className="combined-checkout-summary-row">
                      <span className="combined-checkout-summary-label">Treatments</span>
                      <span className="combined-checkout-summary-value">{formatPrice(treatmentsNetTotal)}</span>
                    </div>
                  )}
                  {hasPricedProducts && (
                    <div className="combined-checkout-summary-row">
                      <span className="combined-checkout-summary-label">Products</span>
                      <span className="combined-checkout-summary-value">{formatPrice(productsNetTotal)}</span>
                    </div>
                  )}

                  {(totalDiscountAmount > 0) && (
                    <div className="combined-checkout-summary-row">
                      <span className="combined-checkout-summary-label">You save</span>
                      <span className="combined-checkout-summary-value">{formatPrice(totalDiscountAmount)}</span>
                    </div>
                  )}

                  <div className="combined-checkout-summary-divider" />

                  <div className="combined-checkout-summary-row combined-checkout-summary-total-row">
                    <span className="combined-checkout-summary-total-label">Total</span>
                    <span className="combined-checkout-summary-total-value">{formatPrice(grandTotal)}</span>
                  </div>
                  <div className="combined-checkout-summary-footnote">Taxes included where applicable</div>
                </>
              ) : (
                <div className="combined-checkout-summary-footnote">Pricing will be shared during consultation.</div>
              )}
            </div>

            <div className="combined-checkout-footer-actions">
              <button
                type="button"
                className="skin-action-button skin-action-button-secondary checkout-modal-cancel-suggestions-button"
                onClick={handleCancelSuggestions}
                disabled={isPlacingOrder}
              >
                {productsOnly ? 'Cancel Checkout' : treatmentsOnly ? 'Cancel Treatments' : 'Cancel Suggestions'}
              </button>
              <button
                type="button"
                className="skin-action-button checkout-modal-place-order-button"
                onClick={handleOpenProceedConfirmModal}
                disabled={isEmpty || isPlacingOrder}
              >
                {isPlacingOrder
                  ? 'Confirming…'
                  : (productsOnly ? 'Proceed to Checkout' : treatmentsOnly ? 'Confirm Treatments' : 'Confirm Suggestions')}
              </button>
              <div className="combined-checkout-trustline" aria-label="Suggestions confirmation">
                {productsOnly
                  ? 'Saved to My Orders • Checkout opens in a new tab'
                  : treatmentsOnly
                    ? 'Saved to partner My Orders'
                    : 'Suggestions only • Instant confirmation'}
              </div>
            </div>
          </div>
        </div>

        {showProceedConfirmModal && (
          <div className="checkout-modal-inline-popup-backdrop" onClick={handleCloseProceedConfirmModal}>
            <div className="checkout-modal-inline-popup" onClick={(e) => e.stopPropagation()}>
              <h3 className="checkout-modal-inline-popup-title">
                {productsOnly ? 'Proceed to Product Checkout?' : treatmentsOnly ? 'Confirm Treatments?' : 'Proceed With Suggestions?'}
              </h3>
              <p className="checkout-modal-inline-popup-text">
                {productsOnly
                  ? 'This order will be saved for tracking, then product checkout will open in a new tab.'
                  : treatmentsOnly
                    ? 'Selected treatments will be sent to partner My Orders.'
                    : 'Please confirm to proceed with these selected suggestions.'}
              </p>
              <div className="checkout-modal-inline-popup-actions">
                <button
                  type="button"
                  className="skin-action-button skin-action-button-secondary checkout-modal-inline-btn"
                  onClick={handleCloseProceedConfirmModal}
                  disabled={isPlacingOrder}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="skin-action-button checkout-modal-inline-btn"
                  onClick={handlePlaceOrder}
                  disabled={isPlacingOrder}
                >
                  {isPlacingOrder ? 'Confirming…' : 'Proceed'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showSuccessModal && (
          <div className="checkout-modal-inline-popup-backdrop" onClick={handleCloseSuccessModal}>
            <div className="checkout-modal-inline-popup checkout-modal-inline-popup-success" onClick={(e) => e.stopPropagation()}>
              <h3 className="checkout-modal-inline-popup-title">
                {productsOnly ? 'Checkout Opened' : treatmentsOnly ? 'Treatments Confirmed' : 'Suggestions Confirmed'}
              </h3>
              <p className="checkout-modal-inline-popup-text">
                {productsOnly
                  ? 'Order saved to My Orders. Product checkout opened in a new tab.'
                  : treatmentsOnly
                    ? 'Treatments were saved to partner My Orders.'
                    : 'Suggestions confirmed successfully!'}
              </p>
              <div className="checkout-modal-inline-popup-actions checkout-modal-inline-popup-actions-center">
                <button
                  type="button"
                  className="skin-action-button checkout-modal-inline-btn"
                  onClick={handleCloseSuccessModal}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    getNaturalsSalonModalRoot()
  );
};

export default CombinedCheckoutModal;

